/**
 * 全局样式注入 —— 三块 CSS 变量 + 少量裸 CSS 的归属地。
 *
 * ⚠️ 这是"在 React 之外"用得到主题的地方（::selection、滚动条、加载态、原生控件）的唯一出口。
 *    组件样式请走 useThemedStyles / useTheme().palette，不要往这里加。
 *
 * 为什么在模块加载时注入、而不是 useEffect：
 *    本模块由 app.tsx 顶层 import，ES 模块求值发生在 React 首次渲染之前，
 *    这样变量在首帧就已就位，不会闪一下无主题的默认样式。
 *
 * 三块结构对应三种状态（与 _v2-designs/jiangnan-qinan.html 一致）：
 *    1. :root                     —— 浅色（默认）
 *    2. @media dark + :not(light) —— 跟随系统（未被手动覆盖时才生效）
 *    3. :root[data-theme="dark"]  —— 手动深色
 * 全部由 tokens.ts 推导，改配色只需改 tokens.ts。
 */

import { dark, light, shadowDark, shadowLight, tokenCssDecls, fonts } from './tokens'

const STYLE_ID = 'sbt-global-style'

export function buildGlobalCss(): string {
	const rootLight = tokenCssDecls(light, shadowLight)
	const rootDark = tokenCssDecls(dark, shadowDark)

	return `
:root {
${rootLight}
	color-scheme: light;
}

@media (prefers-color-scheme: dark) {
	:root:not([data-theme="light"]) {
${rootDark}
		color-scheme: dark;
	}
}

:root[data-theme="dark"] {
${rootDark}
	color-scheme: dark;
}

:root[data-theme="light"] {
	color-scheme: light;
}

html, body {
	background: var(--paper);
	color: var(--ink);
}

body {
	font-family: ${fonts.sans};
	font-size: 15px;
	-webkit-font-smoothing: antialiased;
	text-rendering: optimizeLegibility;
}

h1, h2, h3, h4 {
	font-family: ${fonts.serif};
	font-weight: 600;
	line-height: 1.35;
	text-wrap: balance;
}

a {
	color: var(--celadon-ink);
}

code, kbd, pre, samp {
	font-family: ${fonts.mono};
}

::selection {
	color: inherit;
	background-color: var(--celadon-2);
}

:focus-visible {
	outline: 2px solid var(--celadon-ink);
	outline-offset: 2px;
	border-radius: 2px;
}

/* 滚动条：细一点，用墨线色，避免 Chrome 默认亮条在深色下刺眼 */
* {
	scrollbar-width: thin;
	scrollbar-color: var(--line-2) transparent;
}

*::-webkit-scrollbar {
	width: 10px;
	height: 10px;
}

*::-webkit-scrollbar-track {
	background: transparent;
}

*::-webkit-scrollbar-thumb {
	background: var(--line-2);
	border-radius: 5px;
	border: 2px solid transparent;
	background-clip: content-box;
}

*::-webkit-scrollbar-thumb:hover {
	background: var(--ink-3);
	background-clip: content-box;
}
`.trim()
}

export function ensureGlobalStyle() {
	if (typeof document === 'undefined') return
	if (document.getElementById(STYLE_ID)) return
	const el = document.createElement('style')
	el.id = STYLE_ID
	el.textContent = buildGlobalCss()
	document.head.appendChild(el)
}

ensureGlobalStyle()
